import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

const AuthContext = createContext();

// Role definitions for easier management
const ROLES = {
  ADMIN: "admin",
  PASTOR: "pastor",
  STAFF: "staff",
  MEMBER: "member",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [church, setChurch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  // Fetch church data with error handling
  const fetchChurchData = async (churchId) => {
    try {
      const response = await fetch(`https://api.ofi-cia.com/api/churches/list/${churchId}`);
      if (response.ok) {
        const churchData = await response.json();
        setChurch(churchData);
        localStorage.setItem("church", JSON.stringify(churchData));
        return churchData;
      }
      throw new Error("Failed to fetch church data");
    } catch (error) {
      console.error("Error fetching church data:", error);
      return null;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("authToken");

      if (storedUser && storedToken) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setToken(storedToken);

          // Check if we have church data in localStorage
          const storedChurch = localStorage.getItem("church");
          if (storedChurch) {
            setChurch(JSON.parse(storedChurch));
          } else if (parsedUser.church_id) {
            await fetchChurchData(parsedUser.church_id);
          }
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function with role handling
  const login = async (userData, newToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("authToken", newToken);
    setUser(userData);
    setToken(newToken);

    // console.log(userData.member_id);

    if (userData.church_id) {
      await fetchChurchData(userData.church_id);
    }

    // Redirect based on role
    redirectBasedOnRole(userData.role);
  };

  // Redirect users based on their role
  const redirectBasedOnRole = (role) => {
    switch (role) {
      case ROLES.ADMIN:
        navigate("/admin/dashboard");
        break;
      case ROLES.PASTOR:
        navigate("/pastor/dashboard");
        break;
      case ROLES.STAFF:
        navigate("/staff/dashboard");
        break;
      case ROLES.MEMBER:
        navigate("/member/dashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  // Logout function
  const logout = async () => {
    // API call to invalidate the token on the server
    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    // Use the current token from state, not a hardcoded one
    if (token) {
      myHeaders.append("Authorization", `Bearer ${token}`);
    }
    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      redirect: "follow",
    };

    try {
      await fetch("https://api.ofi-cia.com/api/logout", requestOptions);
    } catch (error) {
      console.error("Error during API logout:", error);
    } finally {
      // Clear all auth-related data from local storage
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      localStorage.removeItem("church");
      localStorage.removeItem("rememberMe");

      // Reset state
      setUser(null);
      setToken(null);
      setChurch(null);

      // Navigate to the sign-in page after logout
      navigate("/authentication/sign-in", { replace: true });
    }
  };

  // Check if user has specific role
  const hasRole = (requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole;
  };

  // Check if user has any of the required roles
  const hasAnyRole = (requiredRoles) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        church,
        isLoading,
        token,
        login,
        logout,
        fetchChurchData,
        isAuthenticated: !!user,
        // Role check helpers
        isAdmin: hasRole(ROLES.ADMIN),
        isPastor: hasRole(ROLES.PASTOR),
        isStaff: hasRole(ROLES.STAFF),
        isMember: hasRole(ROLES.MEMBER),
        hasRole,
        hasAnyRole,
        // Role constants
        ROLES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
